import React, { useCallback } from "react";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import { Info } from "lucide-react";

interface EventActionSelectorProps {
  /** Current restriction type value - true = Restrictive, false = Permissive */
  value: boolean;
  /** Callback when restriction type changes */
  onChange: (value: boolean) => void;
  /** Translation function */
  t: (key: string) => string;
}

/**
 * Renders a rich tooltip label with bold title and description
 */
const RichTooltipLabel: React.FC<{ title: string; description: string }> = ({
  title,
  description,
}) => (
  <Box>
    <Text fontWeight="bold" fontSize="12px" mb="4px">
      {title}
    </Text>
    <Text fontWeight="normal" fontSize="12px">
      {description}
    </Text>
  </Box>
);

/**
 * EventActionSelector - Button-style radio selector for event action type
 * Displays two buttons: "Restritivo" (Restrictive) and "Permissivo" (Permissive)
 * Maintains radio behavior - only one can be selected at a time
 *
 * FR: Moved from Step 3 (InsightPreferencesBlock) to Step 2
 * When "Permissivo" is selected, the risk category selector should be hidden
 */
export const EventActionSelector: React.FC<EventActionSelectorProps> = ({
  value,
  onChange,
  t,
}) => {
  const handleRestrictiveClick = useCallback(() => {
    onChange(true);
  }, [onChange]);

  const handlePermissiveClick = useCallback(() => {
    onChange(false);
  }, [onChange]);

  const selectedStyles = {
    bg: "rgba(183, 227, 252, 0.32)",
    color: "#0884CC",
    borderColor: "#0884CC",
    _hover: { bg: "rgba(183, 227, 252, 0.48)" },
  };

  const unselectedStyles = {
    bg: "white",
    color: "#4A6075",
    borderColor: "#B9C6D3",
    _hover: { bg: "#F8FAFC" },
  };

  return (
    <Box>
      <Flex align="center" gap="6px" mb="16px">
        <Text
          fontSize="14px"
          fontWeight="500"
          color="#242E38"
          lineHeight="24px"
        >
          {t("eventAction")}
        </Text>
        <Tooltip
          label={
            <RichTooltipLabel
              title={t("eventAction")}
              description={t("eventActionTooltip")}
            />
          }
          placement="top"
          hasArrow
          bg="#242E38"
          color="white"
          borderRadius="8px"
          p="10px"
          maxW="240px"
        >
          <Box
            as="span"
            display="inline-flex"
            alignItems="center"
            cursor="pointer"
            lineHeight="0"
            mb="2px"
          >
            <Info size={16} color="#7e7b7b" />
          </Box>
        </Tooltip>
      </Flex>
      <Flex gap="16px">
        {/* Restritivo Button */}
        <Tooltip
          label={
            <RichTooltipLabel
              title={t("restrictive")}
              description={t("restrictiveTooltip")}
            />
          }
          placement="top"
          hasArrow
          bg="white"
          color="#242E38"
          borderRadius="8px"
          p="10px"
          maxW="240px"
          boxShadow="0px 2px 8px rgba(0, 0, 0, 0.25)"
        >
          <Box
            as="button"
            type="button"
            onClick={handleRestrictiveClick}
            flex="1"
            p="10px"
            borderRadius="8px"
            border="1px solid"
            fontSize="14px"
            fontWeight="500"
            cursor="pointer"
            transition="all 0.15s ease"
            textAlign="center"
            overflow="hidden"
            {...(value === true ? selectedStyles : unselectedStyles)}
          >
            {t("restrictive")}
          </Box>
        </Tooltip>

        {/* Permissivo Button */}
        <Tooltip
          label={
            <RichTooltipLabel
              title={t("permissive")}
              description={t("permissiveTooltip")}
            />
          }
          placement="top"
          hasArrow
          bg="white"
          color="#242E38"
          borderRadius="8px"
          p="10px"
          maxW="240px"
          boxShadow="0px 2px 8px rgba(0, 0, 0, 0.25)"
        >
          <Box
            as="button"
            type="button"
            onClick={handlePermissiveClick}
            flex="1"
            p="10px"
            borderRadius="8px"
            border="1px solid"
            fontSize="14px"
            fontWeight="500"
            cursor="pointer"
            transition="all 0.15s ease"
            textAlign="center"
            overflow="hidden"
            {...(value === false ? selectedStyles : unselectedStyles)}
          >
            {t("permissive")}
          </Box>
        </Tooltip>
      </Flex>
    </Box>
  );
};
